import { useEffect, useRef } from 'react';
import { GeoJSONSource } from 'maplibre-gl';
import { GeoJsonPolygon } from '@/utils/geojson';
import { useMapContext } from './MapContext';

export interface MapPolygonProps {
  polygon: GeoJsonPolygon;
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeWidth?: number;
  layerId?: string;
  sourceId?: string;
}

export const MapPolygon = ({
  polygon,
  fillColor = '#E63946',
  fillOpacity = 0.2,
  strokeColor = '#E63946',
  strokeWidth = 2,
  layerId = 'polygon-layer',
  sourceId = 'polygon-source',
}: MapPolygonProps) => {
  const map = useMapContext();
  const sourceRef = useRef<string>(sourceId);
  const layerRef = useRef<string>(layerId);

  useEffect(() => {
    if (!map) {
      return;
    }

    const addSourceAndLayers = () => {
      if (!map.isStyleLoaded()) {
        return;
      }

      const source = map.getSource(sourceRef.current) as GeoJSONSource | undefined;
      if (source) {
        source.setData(polygon as any);
      } else {
        try {
          map.addSource(sourceRef.current, {
            type: 'geojson',
            data: polygon as any,
          });
        } catch (error) {
          console.warn('Failed to add source:', error);
          return;
        }
      }

      if (!map.getLayer(layerRef.current)) {
        try {
          map.addLayer({
            id: layerRef.current,
            type: 'fill',
            source: sourceRef.current,
            paint: {
              'fill-color': fillColor,
              'fill-opacity': fillOpacity,
            },
          });

          map.addLayer({
            id: `${layerRef.current}-outline`,
            type: 'line',
            source: sourceRef.current,
            paint: {
              'line-color': strokeColor,
              'line-width': strokeWidth,
            },
          });
        } catch (error) {
          console.warn('Failed to add layers:', error);
        }
      }
    };

    if (map.isStyleLoaded()) {
      addSourceAndLayers();
    } else {
      const onStyleLoad = () => {
        addSourceAndLayers();
        map.off('styledata', onStyleLoad);
      };
      map.on('styledata', onStyleLoad);
      
      return () => {
        map.off('styledata', onStyleLoad);
      };
    }

    return () => {
      if (!map) {
        return;
      }
      try {
        if (typeof map.getLayer === 'function' && map.getLayer(layerRef.current)) {
          map.removeLayer(layerRef.current);
        }
        if (typeof map.getLayer === 'function' && map.getLayer(`${layerRef.current}-outline`)) {
          map.removeLayer(`${layerRef.current}-outline`);
        }
        if (typeof map.getSource === 'function' && map.getSource(sourceRef.current)) {
          map.removeSource(sourceRef.current);
        }
      } catch (error) {
        console.warn('Error cleaning up MapPolygon:', error);
      }
    };
  }, [map, polygon, fillColor, fillOpacity, strokeColor, strokeWidth]);

  return null;
};
